import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import { ConversationId as ConversationIdSchema } from "@redotech/effect-api-shared/ids/definitions";
import type {
  ConversationListCursor,
  ConversationListItemResponse,
  UpdateConversationInput,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { MessageWithConversationId } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { ConversationThreadResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import type { FilterGroup } from "@redotech/redo-model/views/views";
import { encodeAdvancedFilterGroup } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { toast } from "@redotech/redo-web/alert";
import { parseLoadSubsetOptions } from "@tanstack/db";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { Effect, Option } from "effect";
import { tanstackFiltersToFilterGroup } from "../filters/conversation-tanstack-expressions";
import { catchApiErrors } from "./error-handling";

function toOptionIfPresent<T>(
  value: T | null | undefined,
): Option.Option<T> | undefined {
  if (value === undefined) {
    return undefined;
  }
  return value === null ? Option.none() : Option.some(value);
}

function toUpdateConversationInput(
  changes: Partial<ConversationListItemResponse>,
): UpdateConversationInput {
  return {
    status: changes.status,
    priority: toOptionIfPresent(changes.priority),
    readAt: toOptionIfPresent(changes.readAt),
    mongoAssigneeId: toOptionIfPresent(changes.mongoAssigneeId),
  };
}

export const conversationQueryKeys: {
  readonly all: readonly ["conversations"];
  readonly list: () => readonly ["conversations", "list"];
  readonly threads: () => readonly ["conversations", "threads"];
  readonly messages: () => readonly ["conversations", "messages"];
} = {
  all: ["conversations"] as const,
  list: () => [...conversationQueryKeys.all, "list"] as const,
  threads: () => [...conversationQueryKeys.all, "threads"] as const,
  messages: () => [...conversationQueryKeys.all, "messages"] as const,
};

interface CreateGlobalCollectionsOptions {
  queryClient: QueryClient;
  effectApi: EffectApiContext;
}

const FIVE_MINUTES_MS = 5 * 60 * 1000;
const DEFAULT_PAGE_SIZE = 25;

export interface ConversationFetchResult {
  items: ConversationListItemResponse[];
  nextCursor: ConversationListCursor | null;
}

function createConversationsListCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection<ConversationListItemResponse, ConversationId>(
    queryCollectionOptions({
      queryKey: conversationQueryKeys.list(),
      queryFn: async (ctx): Promise<ConversationListItemResponse[]> => {
        const parsed = parseLoadSubsetOptions(ctx.meta?.loadSubsetOptions);
        const requestedLimit = parsed.limit ?? 1;

        // Convert TanStack filters back to FilterGroup for the backend
        const filterGroup = tanstackFiltersToFilterGroup(parsed.filters);
        const encodedFilterGroup =
          filterGroup.clauses.length > 0
            ? encodeAdvancedFilterGroup(filterGroup)
            : undefined;

        const client = effectApi.getClient();
        const result = await effectApi.runPromise(
          catchApiErrors(
            client.conversation.getFilteredConversations({
              urlParams: {
                limit: requestedLimit,
                filterGroup: encodedFilterGroup,
              },
            }),
          ),
        );

        return [...result.items];
      },
      getKey: (item) => item.id,
      queryClient,
      syncMode: "on-demand",
      staleTime: FIVE_MINUTES_MS,
      onUpdate: async ({ transaction }) => {
        const client = effectApi.getClient();

        for (const mutation of transaction.mutations) {
          await effectApi.runPromise(
            catchApiErrors(
              client.conversation.updateConversation({
                path: { id: ConversationIdSchema.make(mutation.key) },
                payload: toUpdateConversationInput(mutation.changes),
              }),
            ),
          );
        }

        toast("Conversation updated successfully", { variant: "success" });
      },
    }),
  );
}

/**
 * Fetches conversations with cursor-based pagination and optional filter group.
 * This is used alongside the collection for infinite scroll support.
 */
export async function fetchConversationsPage(
  effectApi: EffectApiContext,
  options: {
    limit?: number;
    cursor?: ConversationListCursor;
    filterGroup?: FilterGroup;
  },
): Promise<ConversationFetchResult> {
  const encodedFilterGroup = options.filterGroup
    ? encodeAdvancedFilterGroup(options.filterGroup)
    : undefined;

  const client = effectApi.getClient();
  const result = await effectApi.runPromise(
    catchApiErrors(
      client.conversation.getFilteredConversations({
        urlParams: {
          limit: options.limit ?? DEFAULT_PAGE_SIZE,
          cursor: options.cursor,
          filterGroup: encodedFilterGroup,
        },
      }),
    ),
  );

  return { items: [...result.items], nextCursor: result.nextCursor };
}

function createThreadsCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection<ConversationThreadResponse, string>(
    queryCollectionOptions({
      queryKey: conversationQueryKeys.threads(),
      queryFn: async (ctx): Promise<ConversationThreadResponse[]> => {
        const { filters } = parseLoadSubsetOptions(ctx.meta?.loadSubsetOptions);

        // Extract conversationIds from filters array
        // filters is [{ field: ["conversationId"], operator: "in" | "eq", value: ... }]
        const conversationIdFilter = filters.find(
          (f) => f.field[0] === "conversationId",
        );

        if (!conversationIdFilter?.value) {
          return [];
        }

        const client = effectApi.getClient();

        // if we're getting threads for 1 conversation, then conversationIdFilter.operator is "eq"
        // but it is "in" if we're getting threads for array of conversations
        // This one queryFn needs to handle both cases
        const conversationIds =
          conversationIdFilter.operator === "in"
            ? (conversationIdFilter.value as ConversationId[])
            : [conversationIdFilter.value as ConversationId];

        // Fetch threads for all conversation IDs in parallel
        const results = await effectApi.runPromise(
          // eslint-disable-next-line unicorn/no-array-for-each -- Effect.forEach, not Array.forEach
          Effect.forEach(
            conversationIds,
            (conversationId) =>
              catchApiErrors(
                client.conversation.getConversationThreads({
                  path: { id: ConversationIdSchema.make(conversationId) },
                  urlParams: { limit: DEFAULT_PAGE_SIZE },
                }),
              ).pipe(Effect.map((result) => result.items)),
            { concurrency: "unbounded" },
          ).pipe(Effect.map((results) => results.flat())),
        );
        return results;
      },
      queryClient,
      getKey: (item) => item.id.toString(),
      syncMode: "on-demand",
    }),
  );
}

function createMessagesCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection<MessageWithConversationId, string>(
    queryCollectionOptions({
      queryKey: conversationQueryKeys.messages(),
      queryFn: async (ctx): Promise<MessageWithConversationId[]> => {
        const { filters } = parseLoadSubsetOptions(ctx.meta?.loadSubsetOptions);

        // Extract conversationIds from filters array
        // filters is [{ field: ["conversationId"], operator: "in" | "eq", value: ... }]
        const conversationIdFilter = filters.find(
          (f) => f.field[0] === "conversationId",
        );

        if (!conversationIdFilter?.value) {
          return [];
        }

        const client = effectApi.getClient();

        // Handle both "eq" (single ID) and "in" (array of IDs) operators
        const conversationIds =
          conversationIdFilter.operator === "in"
            ? (conversationIdFilter.value as string[])
            : [conversationIdFilter.value as string];

        // Fetch messages for all conversation IDs in parallel
        const results = await Promise.all(
          conversationIds.map(async (id) => {
            const result = await effectApi.runPromise(
              catchApiErrors(
                client.conversation.getConversationMessages({
                  path: { id: ConversationIdSchema.make(id) },
                  urlParams: { limit: DEFAULT_PAGE_SIZE },
                }),
              ),
            );
            return result.items.map((message) => ({
              ...message,
              conversationId: ConversationIdSchema.make(id),
            }));
          }),
        );

        return results.flat();
      },
      queryClient,
      getKey: (item) => item.id.toString(),
      syncMode: "on-demand",
    }),
  );
}

export type ConversationsListCollection = ReturnType<
  typeof createConversationsListCollection
>;
export type ThreadsCollection = ReturnType<typeof createThreadsCollection>;
export type MessagesCollection = ReturnType<typeof createMessagesCollection>;

export interface GlobalConversationCollections {
  conversationsList: ConversationsListCollection;
  threads: ThreadsCollection;
  messages: MessagesCollection;
}

export function createGlobalConversationCollections(
  options: CreateGlobalCollectionsOptions,
): GlobalConversationCollections {
  const conversationsList = createConversationsListCollection(options);
  const threads = createThreadsCollection(options);
  const messages = createMessagesCollection(options);

  return { conversationsList, threads, messages };
}
